import { useState } from 'react';
import { errorMessage } from '@/core/http';
import { Badge, Button, EmptyState, ErrorState, FilterMenu, Skeleton, type FilterOption } from '@/core/ui';
import { DownloadSimple, LockSimple } from '@/core/ui/icons';
import { useExportResults, useFlowResults } from '../application/queries';
import { formatDateTime } from '../domain/flow';
import { RESULTS_PAGE_SIZE, SESSION_STATUSES, sessionStatus } from '../domain/results';
import type { SessionStatus } from '../domain/types';

/** Baixa o arquivo sem abrir aba: Blob → endereço local → clique. */
function download(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * As conversas do fluxo, uma por linha, com as respostas em colunas.
 * São dados pessoais: ficam no backoffice e na planilha exportada.
 */
export function FlowResults({ flowId }: { flowId: string }) {
  const [status, setStatus] = useState<SessionStatus | 'todos'>('todos');
  const [page, setPage] = useState(1);
  const results = useFlowResults(flowId, {
    status: status === 'todos' ? undefined : status,
    limit: RESULTS_PAGE_SIZE,
    offset: (page - 1) * RESULTS_PAGE_SIZE,
  });
  const exporter = useExportResults();

  const total = results.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / RESULTS_PAGE_SIZE));
  const items = results.data?.items ?? [];
  const variables = results.data?.variables ?? [];
  const options: FilterOption<SessionStatus | 'todos'>[] = [
    { value: 'todos', label: 'Todas' },
    ...SESSION_STATUSES.map((s) => ({ value: s.value, label: s.label })),
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <FilterMenu
          label="Situação"
          value={status}
          options={options}
          onChange={(v) => {
            setStatus(v);
            setPage(1);
          }}
        />
        <Button
          variant="secondary"
          loading={exporter.isPending}
          onClick={() =>
            exporter.mutate(flowId, { onSuccess: ({ blob, filename }) => download(blob, filename) })
          }
        >
          {!exporter.isPending && <DownloadSimple aria-hidden className="size-4" />}
          Exportar CSV
        </Button>
      </div>
      {exporter.isError && (
        <p role="alert" className="text-sm text-fg">
          {errorMessage(exporter.error)}
        </p>
      )}
      <p className="flex items-center gap-1.5 text-xs text-fg-subtle">
        <LockSimple aria-hidden className="size-3.5" />
        Dados pessoais de quem conversou — use só para o atendimento e não compartilhe a planilha fora do
        time.
      </p>

      {results.isPending ? (
        <Skeleton className="h-72 rounded-3xl" />
      ) : results.isError ? (
        <ErrorState error={results.error} onRetry={() => void results.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState
          title={status === 'todos' ? 'Nenhuma conversa ainda' : 'Nenhuma conversa nesta situação'}
          description={
            status === 'todos'
              ? 'Quando alguém fizer este fluxo no portal, as respostas aparecem aqui.'
              : undefined
          }
        />
      ) : (
        <>
          <div className="overflow-x-auto rounded-3xl bg-surface">
            <table className="w-full min-w-[44rem] text-left text-sm">
              <thead>
                <tr className="text-xs text-fg-subtle">
                  <th scope="col" className="px-5 py-3 font-medium">
                    Pessoa
                  </th>
                  <th scope="col" className="px-3 py-3 font-medium whitespace-nowrap">
                    Início
                  </th>
                  <th scope="col" className="px-3 py-3 font-medium whitespace-nowrap">
                    Fim
                  </th>
                  <th scope="col" className="px-3 py-3 font-medium">
                    Situação
                  </th>
                  <th scope="col" className="px-3 py-3 font-medium">
                    Versão
                  </th>
                  {variables.map((v) => (
                    <th key={v} scope="col" className="px-3 py-3 font-mono font-medium whitespace-nowrap">
                      {v}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((r) => {
                  const s = sessionStatus(r.status);
                  return (
                    <tr key={r.session_id} className="align-top odd:bg-surface-2/60">
                      <td className="px-5 py-3">
                        <span className="block font-medium text-fg">{r.name || '—'}</span>
                        <span className="block text-xs text-fg-subtle">{r.email}</span>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-fg-muted">
                        {formatDateTime(r.started_at)}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-fg-muted">
                        {formatDateTime(r.finished_at)}
                      </td>
                      <td className="px-3 py-3">
                        <Badge tone={s.tone} className="whitespace-nowrap">
                          {s.label}
                        </Badge>
                      </td>
                      <td className="px-3 py-3 text-fg-muted">v{r.version}</td>
                      {variables.map((v) => (
                        <td key={v} className="max-w-64 px-3 py-3 break-words text-fg">
                          {r.answers[v] ?? ''}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <nav aria-label="Páginas" className="flex items-center justify-between gap-3 text-sm">
            <span className="text-fg-subtle">
              {(page - 1) * RESULTS_PAGE_SIZE + 1}–{Math.min(page * RESULTS_PAGE_SIZE, total)} de {total}
            </span>
            {pages > 1 && (
              <div className="flex gap-2">
                <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  Anterior
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page >= pages}
                  onClick={() => setPage(page + 1)}
                >
                  Próxima
                </Button>
              </div>
            )}
          </nav>
        </>
      )}
    </div>
  );
}
